import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LoginTracking } from '@mira-avant/mtlibs';
import { DisplayNamePipe } from '../../../../pipes/display-name.pipe';

@Component({
	selector: 'app-login-tracking-list',
	standalone: true,
	imports: [CommonModule, DisplayNamePipe],
	templateUrl: './login-tracking-list.component.html',
	styleUrl: './login-tracking-list.component.scss',
})
export class LoginTrackingListComponent {
	@Input('logins') public logins: LoginTracking[] = [];
	@Input('showUsername') public showUsername: boolean = false;

	ngOnInit() {}

	/**
	 * Format the date portion of the login timestamp
	 */
	formatDate(date: Date | string): string {
		const d = new Date(date);
		return d.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		});
	}

	/**
	 * Format the time portion of the login timestamp
	 */
	formatTime(date: Date | string): string {
		const d = new Date(date);
		return d.toLocaleTimeString('en-US', {
			hour: '2-digit',
			minute: '2-digit',
		});
	}

	/**
	 * Determine device type from fingerprint
	 */
	getDeviceType(login: LoginTracking): string {
		if (!login.device_fingerprint) {
			return 'Unknown Device';
		}

		const userAgent = login.device_fingerprint.split('|')[0];

		if (userAgent.includes('iPhone')) {
			return 'iPhone';
		} else if (userAgent.includes('iPad')) {
			return 'iPad';
		} else if (
			userAgent.includes('Android') &&
			(userAgent.includes('Mobile') || userAgent.includes('Phone'))
		) {
			return 'Android Phone';
		} else if (userAgent.includes('Android')) {
			return 'Android Tablet';
		} else if (userAgent.includes('Macintosh')) {
			return 'Mac';
		} else if (userAgent.includes('Windows')) {
			return 'Windows PC';
		} else if (userAgent.includes('Linux')) {
			return 'Linux';
		}

		return 'Unknown Device';
	}

	/**
	 * Determine CSS class for device icon
	 */
	getDeviceIconClass(login: LoginTracking): string {
		if (!login.device_fingerprint) {
			return 'icon-unknown';
		}

		const userAgent = login.device_fingerprint.split('|')[0];

		if (
			userAgent.includes('iPhone') ||
			(userAgent.includes('Android') &&
				(userAgent.includes('Mobile') || userAgent.includes('Phone')))
		) {
			return 'icon-mobile';
		} else if (
			userAgent.includes('iPad') ||
			(userAgent.includes('Android') && !userAgent.includes('Mobile'))
		) {
			return 'icon-tablet';
		} else if (
			userAgent.includes('Macintosh') ||
			userAgent.includes('Windows') ||
			userAgent.includes('Linux')
		) {
			return 'icon-desktop';
		}

		return 'icon-unknown';
	}

	/**
	 * Extract browser information from fingerprint
	 */
	getBrowserInfo(login: LoginTracking): string {
		if (!login.device_fingerprint) {
			return '';
		}

		const userAgent = login.device_fingerprint.split('|')[0];

		if (userAgent.includes('Chrome/')) {
			return this.extractVersion(userAgent, 'Chrome');
		} else if (userAgent.includes('Firefox/')) {
			return this.extractVersion(userAgent, 'Firefox');
		} else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome')) {
			return this.extractVersion(userAgent, 'Safari');
		} else if (userAgent.includes('Edge/') || userAgent.includes('Edg/')) {
			return this.extractVersion(userAgent, 'Edge');
		}

		return '';
	}

	/**
	 * Extract browser version from user agent string
	 */
	private extractVersion(userAgent: string, browser: string): string {
		let regex: RegExp;

		if (browser === 'Chrome') {
			regex = /Chrome\/([0-9.]+)/;
		} else if (browser === 'Firefox') {
			regex = /Firefox\/([0-9.]+)/;
		} else if (browser === 'Safari') {
			regex = /Version\/([0-9.]+)/;
		} else if (browser === 'Edge') {
			regex = /Edg(?:e)?\/([0-9.]+)/;
		} else {
			return browser;
		}

		const match = userAgent.match(regex);
		if (match && match[1]) {
			const version = match[1].split('.')[0]; // Just get the major version
			return `${browser} ${version}`;
		}

		return browser;
	}

	/**
	 * Mask IP address for privacy
	 */
	maskIpAddress(ip: string): string {
		if (!ip) {
			return 'Unknown';
		}

		// Handle IPv6 (::1 is localhost in IPv6)
		if (ip === '::1') {
			return 'localhost (::1)';
		}

		// Handle regular IPv6
		if (ip.includes(':')) {
			const parts = ip.split(':');
			if (parts.length >= 4) {
				return `${parts[0]}:${parts[1]}:****:****`;
			}
		}

		// Handle IPv4
		if (ip.includes('.')) {
			const parts = ip.split('.');
			if (parts.length === 4) {
				return `${parts[0]}.${parts[1]}.*.*`;
			}
		}

		return ip;
	}
}
